/* eslint-disable no-underscore-dangle */

import mapKeys from 'lodash/mapKeys';

import { AssetTypeCode, RawAssetTypeCode } from 'types/models/asset-type';
import Sample from 'types/models/sample';
import SamplePoint, {
  RawSamplePoint,
  SOIL_MOISTURE_SENSOR_TAG_IDS,
  SOIL_TEMPERATURE_SENSOR_TAG_IDS
} from 'types/models/samplePoint';
import {
  SamplePointStatisticWithAggregates
} from 'types/models/samplePointsStatistic';
import { ErrorCode } from 'types/sample.enum';
import parseRawSamplePoint from 'utils/associated-sample-points/parse-raw-samplepoint';
import dateToISOString from 'utils/date-to-iso-string';
import { Modify } from 'utils/TypeScript/modify';

/**
 * Merge associated soil moisture & temperature samples into a single sample.
 */
export const mergeSoilMoistureAndTempSamples = (
  soilMoistureSample: Sample | undefined,
  soilTemperatureSample: Sample | undefined
): Sample => {
  const primarySample = soilMoistureSample || soilTemperatureSample;

  // Give each sampleDim.dataType a different value so we can tell them apart.
  const sampleDim: Modify<Sample, { dataType: RawAssetTypeCode }>[] = [];
  if (soilMoistureSample) {
    sampleDim.push({
      ...soilMoistureSample,
      dataType: RawAssetTypeCode.SOIL_MOISTURE
    });
  }
  if (soilTemperatureSample) {
    sampleDim.push({
      ...soilTemperatureSample,
      dataType: RawAssetTypeCode.SOIL_TEMP
    });
  }

  return {
    ...primarySample,
    multiDimValues: {
      sampleDim: sampleDim.map(({ rwValue, dataType, date, prevDate, errorCode }) => ({
        rwValue,
        dataType,
        sampleDate: dateToISOString(date),
        prevSampleDate: dateToISOString(prevDate),
        errorCode: errorCode ?? ErrorCode.NO_ERROR
      }))
    },
    // Keep original data of temp sample, so we can recreate it if needed.
    // If there's only one Soil sample (Moisture or Temperature), there's no need to keep _hidden filled.
    // @ts-expect-error - _hidden does not exist in Sample type, but we intentionally add it there
    _hidden: sampleDim.length < 2
      ? undefined
      : soilTemperatureSample
  };
};

/**
 * Takes a raw soil samplePoint, and a parsed soil samplePoint, and merges them
 * into a single parsed samplePoint.
 */
const mergeSoilSamplePoints = (
  rawSoilSamplePoint: RawSamplePoint,
  soilSamplePoint: SamplePoint
): SamplePoint => {
  const rawSamplePointIsMoisture =
    rawSoilSamplePoint.assetTypeId === RawAssetTypeCode.SOIL_MOISTURE;

  const [moisture, temp] = rawSamplePointIsMoisture
    ? [parseRawSamplePoint(rawSoilSamplePoint) as SamplePoint, soilSamplePoint]
    : [soilSamplePoint, parseRawSamplePoint(rawSoilSamplePoint) as SamplePoint];
  return {
    ...moisture,
    lastSampleData:
      moisture.lastSampleData && temp.lastSampleData
        ? mergeSoilMoistureAndTempSamples(
          moisture.lastSampleData as unknown as Sample,
          temp.lastSampleData as unknown as Sample
        )
        : undefined,
    // Keep original data of temp samplepoint, so we can recreate it if needed.
    _hidden: temp
  } as SamplePoint;
};

/**
 * Appends suffix to aggregate keys to be used later in `getSoilSummary`
 */
export const mapSoilAggregates = (
  aggregates: SamplePointStatisticWithAggregates['aggregates'],
  suffix: 'Moisture' | 'Temp'
) => Object.keys(aggregates).reduce((acc, key) => ({
  ...acc,
  [key]: {
    name: aggregates[key].name,
    values: mapKeys(aggregates[key].values, (_, k) => `${k}${suffix}`)
  }
}), {});

/**
 * Merge associated soil moisture & temperature samplePointStatistics into a
 * single samplePointStatistic.
 */
export const mergeSoilSamplePointStatistics = (
  moisture: SamplePointStatisticWithAggregates,
  temp: SamplePointStatisticWithAggregates
) => {
  const moistureAggregates = mapSoilAggregates(moisture.aggregates, 'Moisture');
  const tempAggregates = mapSoilAggregates(temp.aggregates, 'Temp');

  return {
    ...moisture,
    lastSample:
      moisture.lastSample && temp.lastSample
        ? mergeSoilMoistureAndTempSamples(
          moisture.lastSample,
          temp.lastSample
        )
        : moisture.lastSample,
    aggregates: [
      ...new Set([
        ...Object.keys(moistureAggregates),
        ...Object.keys(tempAggregates)
      ])
    ].reduce(
      (acc, key) => ({
        ...acc,
        [key]: {
          name: moistureAggregates[key]?.name ?? tempAggregates[key]?.name ?? '',
          values: {
            ...(moistureAggregates[key]?.values ?? {}),
            ...(tempAggregates[key]?.values ?? {})
          }
        }
      }),
      {}
    ),
    _hidden: temp
  };
};

const isSoilMoisture = (samplePoint: SamplePoint | RawSamplePoint) => (
  [RawAssetTypeCode.SOIL_MOISTURE, AssetTypeCode.SOIL].includes(samplePoint.assetTypeId) &&
  SOIL_MOISTURE_SENSOR_TAG_IDS.includes(samplePoint.sensorTags.id)
);

const isSoilTemperature = (samplePoint: SamplePoint | RawSamplePoint) => (
  [RawAssetTypeCode.SOIL_TEMP, AssetTypeCode.SOIL].includes(samplePoint.assetTypeId) &&
  SOIL_TEMPERATURE_SENSOR_TAG_IDS.includes(samplePoint.sensorTags.id)
);

/**
 * Returns true if the given raw & parsed soil samplePoints are associated.
 */
const isMatchingSoilSamplePoint = (
  rawSamplePoint: RawSamplePoint,
  samplePoint: SamplePoint
) => {
  const isSoil = (
    rawSamplePoint.assetTypeId === RawAssetTypeCode.SOIL_MOISTURE ||
    rawSamplePoint.assetTypeId === RawAssetTypeCode.SOIL_TEMP
  ) && samplePoint.assetTypeId === AssetTypeCode.SOIL;

  const isSoilPair = (
    (isSoilMoisture(rawSamplePoint) && isSoilTemperature(samplePoint)) ||
    (isSoilMoisture(samplePoint) && isSoilTemperature(rawSamplePoint))
  );

  const isSameMonitor = rawSamplePoint.deviceId === samplePoint.deviceId;

  return isSoil && isSoilPair && isSameMonitor;
};

/**
 * Takes an array of parsed samplePoints and a raw samplePoint, and
 * returns an array of parsed samplePoints.
 */
export const soilSamplePointReducer = (
  accumulatedSamplePoints: SamplePoint[],
  currentSoil: RawSamplePoint
): SamplePoint[] => {
  const nextSamplePoints: SamplePoint[] = [];
  let merged = false;

  // Scan through the accumulated parsed sample points, if currentSoil
  // pairs with an accumulated sample point, merge them. Otherwise,
  // keep copying SP from accumulatedSamplePoints to nextSamplePoints array.
  for (let i = 0; i < accumulatedSamplePoints.length; i++) {
    const samplePoint = accumulatedSamplePoints[i];

    if (isMatchingSoilSamplePoint(currentSoil, samplePoint)) {
      nextSamplePoints.push(
        mergeSoilSamplePoints(currentSoil, samplePoint)
      );
      merged = true;
    } else {
      nextSamplePoints.push(samplePoint);
    }
  }
  // If currentSoil hasn't paired with any SP, parse it then add to the parsed sample points.
  if (!merged) {
    const parsedSoil = parseRawSamplePoint(currentSoil);
    if (parsedSoil) {
      nextSamplePoints.push(parsedSoil);
    }
  }

  return nextSamplePoints;
};
